import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Briefcase,
  CheckCircle2,
  AlertOctagon,
  CalendarClock,
  Plus,
  ArrowRight,
  BellRing,
  X,
  Inbox,
  TrendingUp,
  Repeat,
  Download,
  Upload,
  ShieldCheck,
  DollarSign,
  Trash2,
  Pencil,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const CATEGORY_LABELS = {
  digital_marketing: "Digital Marketing",
  software_development: "Software Development",
  creative_services: "Creative Services",
  deepthree_assist: "DeepThree Assist",
  deepthree_deliver: "DeepThree Deliver",
};
const PRIORITY_BADGE = ["", "$", "$$", "$$$", "$$$$", "$$$$$"];

const STAGE_COLORS = {
  sales_pipeline: "#71717A",
  verbal_commitment: "#F59E0B",
  in_progress: "#0066ff",
  awaiting_payment: "#7C3AED",
  paid: "#10B981",
};

function money(v) {
  if (typeof v !== "number") return "$0";
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function StatCard({ label, value, icon: Icon, accent, testId }) {
  return (
    <div data-testid={testId} className="dt-card p-5 dt-fade-in">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
          <div className="mt-2 text-3xl font-semibold tracking-tight text-zinc-900">{value}</div>
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${accent}`}>
          <Icon size={18} strokeWidth={1.6} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard/stats");
      setStats(data);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { load(); }, []);

  const dismissFollowUp = async (projectId) => {
    try {
      await api.post(`/follow-ups/${projectId}/dismiss`);
      toast.success("Dismissed");
      load();
    } catch {
      toast.error("Failed to dismiss");
    }
  };

  if (!stats) return <div className="text-sm text-zinc-500" data-testid="dashboard-loading">Loading…</div>;

  const followUps = stats.follow_ups || [];
  const subsDue = stats.subscriptions_due_today || [];
  const subsManualDue = subsDue.filter((s) => s.action_required);

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Overview</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">Dashboard</h1>
          <p className="mt-2 text-sm text-zinc-600">Your studio at a glance.</p>
        </div>
      {isAdmin && <BackupRestore />}
      </div>

      {/* New work orders banner */}
      {stats.new_tickets_count > 0 && (
        <div data-testid="new-orders-banner" className="mt-6 flex items-center justify-between rounded-xl border border-[#0066ff]/30 bg-[#E5F0FF]/60 p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0066ff] text-white">
              <Inbox size={18} />
            </div>
            <div>
              <div className="text-sm font-semibold text-[#0066ff]">
                You have {stats.new_tickets_count} new work order{stats.new_tickets_count > 1 ? "s" : ""}.
              </div>
              <div className="text-xs text-zinc-600">Review them on the Work Orders tab.</div>
            </div>
          </div>
          <button data-testid="new-orders-go" onClick={() => navigate("/work-orders")} className="inline-flex items-center gap-1.5 rounded-md bg-[#0066ff] px-3 py-2 text-sm font-medium text-white hover:bg-[#0052cc]">
            Go to Work Orders <ArrowRight size={14} />
          </button>
        </div>
      )}

      {/* Today's follow-ups */}
      {followUps.length > 0 && (
        <div data-testid="follow-ups-banner" className="mt-6 rounded-xl border border-amber-200 bg-amber-50/60 p-5">
          <div className="flex items-center gap-2">
            <BellRing size={16} className="text-amber-600" />
            <h2 className="text-sm font-semibold text-amber-900">Follow-ups for today</h2>
            <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">{followUps.length}</span>
          </div>
          <div className="mt-3 flex flex-col gap-2">
            {followUps.map((p) => (
              <div key={p.id} data-testid={`follow-up-${p.id}`} className="flex items-center justify-between gap-3 rounded-md bg-white px-3 py-2 text-sm shadow-sm">
                <button onClick={() => navigate(`/projects/${p.id}`)} className="min-w-0 flex-1 text-left">
                  <div className="truncate font-medium text-zinc-900">{p.title}</div>
                  <div className="truncate text-xs text-zinc-500">
                    {CATEGORY_LABELS[p.category] || p.category}
                    {p.follow_up_note ? ` · ${p.follow_up_note}` : ""}
                  </div>
                </button>
                <button
                  data-testid={`follow-up-dismiss-${p.id}`}
                  onClick={() => dismissFollowUp(p.id)}
                  className="rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
                  title="Dismiss"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subscriptions due today (manual invoicing → action required) */}
      {subsManualDue.length > 0 && (
        <div data-testid="subscriptions-due-banner" className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50/60 p-5">
          <div className="flex items-center gap-2">
            <Repeat size={16} className="text-emerald-700" />
            <h2 className="text-sm font-semibold text-emerald-900">Subscriptions due today — manual invoice</h2>
            <span className="ml-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">{subsManualDue.length}</span>
            <button
              data-testid="subscriptions-due-open"
              onClick={() => navigate("/subscriptions")}
              className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-emerald-800 hover:underline"
            >
              Manage <ArrowRight size={12} />
            </button>
          </div>
          <div className="mt-3 flex flex-col gap-2">
            {subsManualDue.map((s) => (
              <button
                key={s.id}
                data-testid={`subs-due-${s.id}`}
                onClick={() => navigate("/subscriptions")}
                className="flex items-center justify-between gap-3 rounded-md bg-white px-3 py-2 text-left text-sm shadow-sm hover:bg-emerald-50/40"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: s.section_color }} />
                  <div className="min-w-0">
                    <div className="truncate font-medium text-zinc-900">{s.client_name}</div>
                    <div className="truncate text-xs text-zinc-500">{s.section_name || "Subscription"}</div>
                  </div>
                </div>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">{money(s.monthly_amount)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6"></div>

      {isAdmin && <RevenueTracker />}
      {!isAdmin && user?.role === "viewer" && <RevenueTracker readOnly />}
      {(isAdmin || user?.role === "viewer") && <LeadSourcePie />}
    </div>
  );
}

function LeadSourcePie() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects/source-breakdown");
        setData(data);
      } catch { /* noop */ }
    })();
  }, []);

  if (!data || !data.total) return null;

  const PALETTE = ["#0066FF", "#10B981", "#F59E0B", "#EF4444", "#7C3AED", "#EC4899", "#06B6D4", "#71717A", "#84CC16"];
  const size = 200; const cx = size / 2; const cy = size / 2; const r = 80;
  let cursor = -Math.PI / 2;
  const arcs = data.items.map((it, i) => {
    const pct = it.count / data.total;
    const start = cursor; const end = cursor + pct * Math.PI * 2;
    cursor = end;
    const x1 = cx + r * Math.cos(start), y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end),   y2 = cy + r * Math.sin(end);
    const large = pct > 0.5 ? 1 : 0;
    const d = `M ${cx} ${cy} L ${x1.toFixed(1)} ${y1.toFixed(1)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(1)} ${y2.toFixed(1)} Z`;
    return { d, color: PALETTE[i % PALETTE.length], source: it.source, count: it.count, pct };
  });

  return (
    <div data-testid="dashboard-source-pie" className="mt-6 rounded-xl border border-zinc-200 bg-white p-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#0066ff]">
            <TrendingUp size={12} /> Lead Sources
          </div>
          <h2 className="mt-1 text-lg font-semibold tracking-tight text-zinc-900">Where projects come from</h2>
          <p className="mt-1 text-xs text-zinc-500">Aggregated across every project's Source field.</p>
        </div>
        <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-700">
          {data.total} project{data.total === 1 ? "" : "s"}
        </span>
      </div>

      <div className="mt-6 flex flex-col items-center gap-6 md:flex-row md:items-start">
        <svg viewBox={`0 0 ${size} ${size}`} className="h-48 w-48 flex-shrink-0" role="img" aria-label="Lead source breakdown">
          {arcs.map((a, i) => (
            <path key={i} d={a.d} fill={a.color} stroke="white" strokeWidth="1.5">
              <title>{`${a.source}: ${a.count} (${(a.pct * 100).toFixed(0)}%)`}</title>
            </path>
          ))}
        </svg>

        <div className="flex w-full flex-col gap-2">
          {arcs.map((a) => (
            <div key={a.source} className="flex items-center justify-between gap-3 text-sm">
              <div className="flex min-w-0 items-center gap-2">
                <span className="inline-block h-3 w-3 flex-shrink-0 rounded-sm" style={{ backgroundColor: a.color }} />
                <span className="truncate text-zinc-700">{a.source}</span>
              </div>
              <span className="font-mono text-xs font-semibold text-zinc-500">
                {a.count} · {(a.pct * 100).toFixed(0)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function PipelineForecastCard({ forecast, weightedRevenue, totalRevenue, onOpen }) {
  const maxWeighted = Math.max(...forecast.map((s) => s.weighted_revenue), 1);
  return (
    <div data-testid="pipeline-forecast-card" className="mt-6 rounded-xl border border-zinc-200 bg-white p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">
            <TrendingUp size={12} className="text-[#0066ff]" /> Pipeline Forecast
          </div>
          <h2 className="mt-1 text-lg font-semibold tracking-tight text-zinc-900">Weighted by Confidence</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <ForecastStat label="Unweighted Total" value={money(totalRevenue)} accent="text-zinc-700" />
          <ForecastStat label="Expected Revenue" value={money(weightedRevenue)} accent="text-[#0066ff]" testId="forecast-weighted-revenue" />
          <button data-testid="forecast-open-pipeline" onClick={onOpen} className="inline-flex items-center gap-1.5 self-center rounded-md px-3 py-2 text-xs font-medium text-[#0066ff] hover:bg-[#E5F0FF]">
            Open <ArrowRight size={12} />
          </button>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3">
        {forecast.map((s) => {
          const pct = (s.weighted_revenue / maxWeighted) * 100;
          const color = STAGE_COLORS[s.stage] || "#71717A";
          return (
            <div key={s.stage} data-testid={`forecast-stage-${s.stage}`} className="grid grid-cols-12 items-center gap-3 text-xs">
              <div className="col-span-3 truncate font-medium text-zinc-700" style={{ color }}>
                {s.label}
                <span className="ml-1 text-[10px] font-normal text-zinc-400">({s.count})</span>
              </div>
              <div className="col-span-6 h-3 overflow-hidden rounded-full bg-zinc-100">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${pct}%`, backgroundColor: color }}
                />
              </div>
              <div className="col-span-3 flex flex-shrink-0 justify-end text-right">
                <span className="font-semibold text-zinc-900">{money(s.weighted_revenue)}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center justify-end border-t border-zinc-100 pt-3 text-[10px] uppercase tracking-widest text-zinc-400">
        <span>Expected Revenue</span>
      </div>
    </div>
  );
}

function ForecastStat({ label, value, accent = "text-zinc-900", testId }) {
  return (
    <div data-testid={testId} className="rounded-md border border-zinc-100 bg-zinc-50/50 px-3 py-2">
      <div className="text-[9px] font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`mt-0.5 text-sm font-semibold ${accent}`}>{value}</div>
    </div>
  );
}

function BackupRestore() {
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const fileRef = React.useRef(null);

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/backup/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `deepthree-backup-${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backup failed");
    } finally {
      setDownloading(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(
      `Restore from "${file.name}"?\n\nThis will UPSERT every row by id — existing matches are updated, new rows are added.\nNOTHING will be deleted.`
    )) {
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/backup/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const totals = Object.values(data.report || {}).reduce(
        (acc, r) => ({ up: acc.up + (r.upserted || 0), skip: acc.skip + (r.skipped || 0) + (r.no_id || 0) }),
        { up: 0, skip: 0 }
      );
      toast.success(`Restored: ${totals.up} rows updated/added · ${totals.skip} skipped`, { duration: 6000 });
      setShowImport(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid="backup-restore-panel" className="flex items-center gap-2">
      <button
        data-testid="dashboard-backup-download"
        onClick={downloadBackup}
        disabled={downloading}
        className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-2 text-xs font-medium text-zinc-700 hover:border-[#0066ff] hover:text-[#0066ff] disabled:opacity-50"
        title="Download an Excel backup of every collection on this site"
      >
        <Download size={13} /> {downloading ? "Preparing…" : "Backup (.xlsx)"}
      </button>
      <button
        data-testid="dashboard-backup-restore"
        onClick={() => setShowImport(true)}
        className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-2 text-xs font-medium text-zinc-700 hover:border-[#0066ff] hover:text-[#0066ff]"
        title="Restore from an Excel backup (upsert by id, never deletes)"
      >
        <Upload size={13} /> Restore
      </button>

      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => !uploading && setShowImport(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-7 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                  <ShieldCheck size={16} />
                </div>
                <h2 className="text-xl font-semibold tracking-tight text-zinc-900">Restore from Backup</h2>
              </div>
              <button onClick={() => !uploading && setShowImport(false)} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
            </div>
            <p className="mt-4 text-sm text-zinc-600">
              Choose a <strong>.xlsx</strong> file produced by the Backup button. Existing rows are matched by their <code className="rounded bg-zinc-100 px-1 text-[11px]">id</code> and updated; new rows are added. <strong>Nothing is ever deleted.</strong>
            </p>
            <div className="mt-5 rounded-md border border-amber-200 bg-amber-50/60 p-3 text-[11px] text-amber-800">
              Tip: it's safe to import the same file twice — the result is the same.
            </div>
            <input
              ref={fileRef}
              type="file"
              data-testid="backup-restore-file"
              accept=".xlsx"
              onChange={handleFile}
              disabled={uploading}
              className="mt-5 block w-full cursor-pointer text-sm text-zinc-700 file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-[#0066ff] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-[#0052cc] disabled:opacity-50"
            />
            {uploading && <div className="mt-3 text-xs text-zinc-500">Restoring… please don't close this window.</div>}
          </div>
        </div>
      )}
    </div>
  );
}


function moneyExact(v) {
  if (typeof v !== "number") return "$0";
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function MONTH_LABEL(key) {
  if (!key || key.length < 7) return key || "";
  const [y, m] = key.split("-");
  const d = new Date(parseInt(y), parseInt(m) - 1, 1);
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function RevenueTracker({ readOnly = false }) {
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const todayKey = new Date().toISOString().slice(0, 7);
  const [selectedMonth, setSelectedMonth] = useState(todayKey);
  const [entries, setEntries] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const loadSummary = async () => {
    try {
      const { data } = await api.get("/revenue/summary");
      setSummary(data);
    } catch { /* noop */ }
  };

  const loadExpenses = async () => {
    try {
      const { data } = await api.get("/expenses/summary");
      setExpenses(data);
    } catch { /* noop */ }
  };

  const loadEntries = async (month) => {
    try {
      const { data } = await api.get(`/revenue/entries?month=${month}`);
      setEntries(data || []);
    } catch { /* noop */ }
  };

  useEffect(() => { loadSummary(); loadExpenses(); }, []);
  useEffect(() => { loadEntries(selectedMonth); }, [selectedMonth]);

  if (!summary) return null;
  const goal = summary.goal || 10000;
  const isCurrent = selectedMonth === summary.current_month;
  const selectedTotal = (summary.months.find((m) => m.month === selectedMonth)?.total) || 0;
  const displayTotal = isCurrent ? summary.current_total : selectedTotal;
  const pct = Math.max(0, Math.min(100, (displayTotal / goal) * 100));

  const refresh = async () => { await loadSummary(); await loadExpenses(); await loadEntries(selectedMonth); };

  const monthOptions = (() => {
    const set = new Set([todayKey, summary.current_month]);
    summary.months.forEach((m) => set.add(m.month));
    // Also include the last 24 months so admin can add entries to older months.
    const [y, m] = todayKey.split("-").map((n) => parseInt(n));
    for (let i = 0; i < 24; i++) {
      let mm = m - i;
      let yy = y;
      while (mm <= 0) { mm += 12; yy -= 1; }
      set.add(`${yy}-${String(mm).padStart(2, "0")}`);
    }
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  })();

  return (
    <div data-testid="dashboard-revenue-card" className="mt-6 rounded-xl border border-zinc-200 bg-white p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-700">
            <DollarSign size={12} /> Revenue
          </div>
          <h2 className="mt-1 text-lg font-semibold tracking-tight text-zinc-900">
            {isCurrent ? "This Month's Revenue" : `${MONTH_LABEL(selectedMonth)} Revenue`}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="revenue-month-select"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="rounded-md border border-zinc-200 bg-white px-3 py-2 text-xs font-medium text-zinc-700 focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
          >
            {monthOptions.map((m) => (
              <option key={m} value={m}>
                {MONTH_LABEL(m)}{m === summary.current_month ? " (current)" : ""}
              </option>
            ))}
          </select>
          {!readOnly && (
            <button
              data-testid="revenue-add"
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-600"
              title={isCurrent ? "Log revenue for this month" : `Log revenue for ${MONTH_LABEL(selectedMonth)}`}
            >
              <Plus size={12} /> Add{!isCurrent && ` to ${MONTH_LABEL(selectedMonth).split(" ")[0]}`}
            </button>
          )}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-[1fr_auto]">
        <div className="rounded-xl bg-gradient-to-br from-emerald-50/60 to-white p-5">
          <div data-testid="revenue-total" className="text-4xl font-semibold tracking-tight text-emerald-700">
            {moneyExact(displayTotal)}
          </div>
          <div className="mt-1 text-xs text-zinc-500">
            of <strong className="text-zinc-700">{moneyExact(goal)}</strong> goal
            {isCurrent
              ? ` · ${pct.toFixed(0)}% there${pct >= 100 ? " 🎉" : ""}`
              : ` · ${pct.toFixed(0)}%`}
          </div>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-zinc-100">
            <div
              data-testid="revenue-bar-horizontal"
              className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Vertical bar */}
        <div className="flex items-end justify-center gap-3">
          <div className="text-right text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
            <div>{moneyExact(goal)}</div>
            <div className="mt-[140px]">$0</div>
          </div>
          <div className="relative flex h-44 w-14 flex-col-reverse overflow-hidden rounded-lg border border-zinc-200 bg-zinc-50">
            <div
              data-testid="revenue-bar-vertical"
              className="w-full rounded-t-lg bg-gradient-to-t from-emerald-500 to-emerald-300 transition-all"
              style={{ height: `${pct}%` }}
              title={`${pct.toFixed(0)}% of goal`}
            />
            {/* Goal-line markers every 25% */}
            {[25, 50, 75].map((p) => (
              <div key={p} className="pointer-events-none absolute left-0 right-0 border-t border-dashed border-zinc-300/70" style={{ bottom: `${p}%` }} />
            ))}
          </div>
        </div>
      </div>

      {/* History toggle */}
      <div className="mt-5 flex items-center justify-between border-t border-zinc-100 pt-4">
        <button
          data-testid="revenue-toggle-entries"
          onClick={() => setShowHistory((s) => !s)}
          className="text-xs font-medium text-[#0066ff] hover:underline"
        >
          {showHistory ? "Hide" : "Show"} {entries.length} entr{entries.length === 1 ? "y" : "ies"} for {MONTH_LABEL(selectedMonth)}
        </button>
      </div>

      {showHistory && (
        <div className="mt-3 flex flex-col gap-2">
          {entries.length === 0 ? (
            <div className="rounded-md border border-dashed border-zinc-200 px-4 py-6 text-center text-xs text-zinc-400">
              No entries for {MONTH_LABEL(selectedMonth)}.
            </div>
          ) : entries.map((e) => (
            <div key={e.id} className="group flex items-center justify-between rounded-md border border-zinc-100 bg-white px-3 py-2 text-sm hover:border-zinc-200 hover:bg-zinc-50/50">
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-zinc-900">{e.label || "Revenue"}</div>
                <div className="text-[11px] text-zinc-500">{e.date}</div>
              </div>
              <div className="ml-3 font-mono font-semibold text-emerald-700">{moneyExact(e.amount)}</div>
              {!readOnly && (
                <div className="ml-3 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    onClick={() => setEditing(e)}
                    className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                    title="Edit"
                  >
                    <Pencil size={12} />
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm("Delete this entry?")) return;
                      await api.delete(`/revenue/entries/${e.id}`);
                      toast.success("Deleted");
                      refresh();
                    }}
                    className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Monthly Expenses — one static number per month (admin only) */}
      <ExpensesBlock
        summary={expenses}
        selectedMonth={selectedMonth}
        readOnly={readOnly}
        onSaved={loadExpenses}
      />

      {/* Year-to-date summary — sums every entry in the current calendar year. */}
      <YTDBlock
        year={summary.current_year}
        ytdTotal={summary.ytd_total || 0}
        ytdExpenses={expenses?.ytd_total || 0}
        goal={summary.yearly_goal || 125000}
        months={summary.months || []}
      />

      {/* Trend line — month-by-month revenue for the current year */}
      <RevenueTrendChart
        year={summary.current_year}
        months={summary.months || []}
        expenseMonths={expenses?.months || []}
      />

      {(adding || editing) && (
        <RevenueModal
          initial={editing}
          defaultMonth={isCurrent ? null : selectedMonth}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={() => { setAdding(false); setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function YTDBlock({ year, ytdTotal, ytdExpenses, goal, months }) {
  const pct = Math.max(0, Math.min(100, (ytdTotal / goal) * 100));
  const remaining = Math.max(0, goal - ytdTotal);
  const monthsLogged = months.filter((m) => (m.month || "").startsWith(`${year}-`)).length;
  const expenses = Math.max(0, Number(ytdExpenses) || 0);
  const expensesPct = Math.max(0, Math.min(100, ytdTotal > 0 ? (expenses / Math.max(ytdTotal, goal)) * 100 : 0));
  const netProfit = ytdTotal - expenses;

  return (
    <div data-testid="dashboard-ytd-card" className="mt-4 rounded-xl border border-zinc-200 bg-white p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-700">
            <DollarSign size={12} /> Year to date · {year}
          </div>
          <h2 className="mt-1 text-lg font-semibold tracking-tight text-zinc-900">{year} Revenue</h2>
        </div>
        <div className="text-right">
          <div data-testid="revenue-ytd-total" className="text-3xl font-semibold tracking-tight text-emerald-700">
            {moneyExact(ytdTotal)}
          </div>
          <div className="mt-0.5 text-xs text-zinc-500">
            of <strong className="text-zinc-700">{moneyExact(goal)}</strong> goal · {pct.toFixed(0)}%
            {pct >= 100 ? " 🎉" : ""}
          </div>
        </div>
      </div>

      <div className="mt-4 h-2.5 w-full overflow-hidden rounded-full bg-zinc-100">
        <div
          data-testid="revenue-ytd-bar"
          className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* YTD Expenses (blue) */}
      <div className="mt-5 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#0066ff]">
          <TrendingUp size={12} /> {year} Expenses
        </div>
        <div data-testid="expenses-ytd-total" className="text-xl font-semibold tracking-tight text-[#0066ff]">
          {moneyExact(expenses)}
        </div>
      </div>
      <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-zinc-100">
        <div
          data-testid="expenses-ytd-bar"
          className="h-full rounded-full bg-gradient-to-r from-[#3388ff] to-[#0066ff] transition-all"
          style={{ width: `${expensesPct}%` }}
        />
      </div>

      {/* Net profit */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-zinc-50 px-4 py-3">
        <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Net Profit YTD</span>
        <span
          data-testid="ytd-net-profit"
          className={`text-2xl font-semibold tracking-tight ${netProfit >= 0 ? "text-emerald-700" : "text-red-600"}`}
        >
          {netProfit < 0 ? `-${moneyExact(Math.abs(netProfit))}` : moneyExact(netProfit)}
        </span>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-zinc-500">
        <span>{monthsLogged} month{monthsLogged === 1 ? "" : "s"} logged this year</span>
        {remaining > 0 && <span><strong className="text-zinc-700">{moneyExact(remaining)}</strong> to goal</span>}
      </div>
    </div>
  );
}

function ExpensesBlock({ summary, selectedMonth, readOnly, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState("");
  const [entryType, setEntryType] = useState("standard");
  const [editing, setEditing] = useState(false);
  const row = useMemo(() => {
    if (!summary) return null;
    return (summary.months || []).find((m) => m.month === selectedMonth) || null;
  }, [summary, selectedMonth]);
  const currentTotal = row ? Number(row.total) || 0 : 0;
  const stdTotal = row ? Number(row.standard) || 0 : 0;
  const spcTotal = row ? Number(row.special) || 0 : 0;
  const currentForType = entryType === "special" ? spcTotal : stdTotal;

  useEffect(() => {
    setEditing(false);
    setDraft(currentForType > 0 ? String(currentForType) : "");
  }, [selectedMonth, currentForType]);

  const save = async () => {
    const amt = parseFloat(draft);
    if (isNaN(amt) || amt < 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      await api.post("/expenses", { month: selectedMonth, amount: amt, entry_type: entryType });
      toast.success("Expenses saved");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="dashboard-expenses-card" className="mt-4 rounded-xl border border-zinc-200 bg-white p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#0066ff]">
            <TrendingUp size={12} /> Expenses
          </div>
          <h2 className="mt-1 text-lg font-semibold tracking-tight text-zinc-900">
            {MONTH_LABEL(selectedMonth)} Expenses
          </h2>
          <p className="mt-1 text-xs text-zinc-500">Standard + Special tracked separately — one total per type per month.</p>
        </div>
        <div className="text-right">
          <div data-testid="expenses-current-total" className="text-3xl font-semibold tracking-tight text-[#0066ff]">
            {moneyExact(currentTotal)}
          </div>
          <div className="mt-0.5 text-[11px] text-zinc-500">
            <span className="text-[#0066ff]">Std {moneyExact(stdTotal)}</span> · <span className="text-[#F59E0B]">Special {moneyExact(spcTotal)}</span>
          </div>
        </div>
      </div>

      {!readOnly && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {!editing ? (
            <>
              <button
                data-testid="expenses-edit-standard"
                onClick={() => { setEntryType("standard"); setDraft(stdTotal > 0 ? String(stdTotal) : ""); setEditing(true); }}
                className="inline-flex items-center gap-1.5 rounded-md bg-[#0066ff] px-3 py-2 text-xs font-semibold text-white hover:bg-[#0052cc]"
              >
                <Pencil size={12} /> {stdTotal > 0 ? "Update" : "Set"} Standard
              </button>
              <button
                data-testid="expenses-edit-special"
                onClick={() => { setEntryType("special"); setDraft(spcTotal > 0 ? String(spcTotal) : ""); setEditing(true); }}
                className="inline-flex items-center gap-1.5 rounded-md bg-[#F59E0B] px-3 py-2 text-xs font-semibold text-white hover:bg-[#D97706]"
              >
                <Pencil size={12} /> {spcTotal > 0 ? "Update" : "Set"} Special
              </button>
            </>
          ) : (
            <>
              <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold text-white" style={{ backgroundColor: entryType === "special" ? "#F59E0B" : "#0066FF" }}>
                {entryType.toUpperCase()}
              </span>
              <span className="text-sm text-zinc-500">$</span>
              <input
                data-testid="expenses-input"
                autoFocus
                type="number" step="0.01" min="0"
                value={draft}
                placeholder="0.00"
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setEditing(false); } }}
                className="w-40 rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
              />
              <button
                data-testid="expenses-save"
                onClick={save}
                disabled={saving}
                className="rounded-md bg-[#0066ff] px-3 py-2 text-xs font-semibold text-white hover:bg-[#0052cc] disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="rounded-md px-3 py-2 text-xs font-medium text-zinc-600 hover:bg-zinc-100"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function RevenueTrendChart({ year, months, expenseMonths = [] }) {
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const monthFull = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const [hoverIdx, setHoverIdx] = useState(null);
  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const [tipPos, setTipPos] = useState({ left: 0, top: 0 });

  // Build 12-slot arrays of totals for the year (6 series total).
  const monthArr = (arr, field) => Array.from({ length: 12 }, (_, i) => {
    const key = `${year}-${String(i + 1).padStart(2, "0")}`;
    const found = (arr || []).find((m) => m.month === key);
    return found ? Number(found[field] || 0) : 0;
  });
  const stdRev = monthArr(months, "standard");
  const spcRev = monthArr(months, "special");
  const totRev = monthArr(months, "total");
  const stdExp = monthArr(expenseMonths, "standard");
  const spcExp = monthArr(expenseMonths, "special");
  const totExp = monthArr(expenseMonths, "total");

  const revTotals = totRev; // used for hover ring on total revenue
  const expTotals = totExp; // used for hover ring on total expenses
  const maxVal = Math.max(...totRev, ...totExp, ...stdRev, ...spcRev, ...stdExp, ...spcExp, 1);
  // niceMax rounds up to next 1k / 5k / 10k step for readable Y axis labels
  const niceStep = maxVal <= 5000 ? 1000 : maxVal <= 25000 ? 5000 : maxVal <= 100000 ? 10000 : 25000;
  const niceMax = Math.ceil(maxVal / niceStep) * niceStep || niceStep;

  const W = 700; const H = 200;
  const padL = 46; const padR = 12; const padT = 12; const padB = 30;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const xFor = (i) => padL + (innerW * (i / 11));
  const yFor = (v) => padT + innerH - (innerH * (v / niceMax));

  const buildPath = (arr) => arr.map((v, i) => `${i === 0 ? "M" : "L"} ${xFor(i).toFixed(1)} ${yFor(v).toFixed(1)}`).join(" ");
  const totRevD = buildPath(totRev);
  const areaD = `${totRevD} L ${xFor(11).toFixed(1)} ${(padT + innerH).toFixed(1)} L ${xFor(0).toFixed(1)} ${(padT + innerH).toFixed(1)} Z`;
  const totExpD = buildPath(totExp);
  const stdRevD = buildPath(stdRev);
  const spcRevD = buildPath(spcRev);
  const stdExpD = buildPath(stdExp);
  const spcExpD = buildPath(spcExp);

  // The 6 series drawn on the chart (order = paint order; totals drawn last so they sit on top).
  const SERIES = [
    { key: "std_rev", label: "Standard revenue", color: "#0066FF", dash: "0",     path: stdRevD, values: stdRev },
    { key: "std_exp", label: "Standard expenses", color: "#93C5FD", dash: "4 3", path: stdExpD, values: stdExp },
    { key: "spc_rev", label: "Special revenue", color: "#F59E0B", dash: "0",     path: spcRevD, values: spcRev },
    { key: "spc_exp", label: "Special expenses", color: "#FDE68A", dash: "4 3", path: spcExpD, values: spcExp },
    { key: "tot_rev", label: "Total revenue", color: "#10B981", dash: "0",       path: totRevD, values: totRev },
    { key: "tot_exp", label: "Total expenses", color: "#EF4444", dash: "0",      path: totExpD, values: totExp },
  ];

  // Gridlines at 0, 25, 50, 75, 100% of niceMax.
  const gridSteps = [0, 0.25, 0.5, 0.75, 1];

  // Convert mouse coords → nearest month index and place the DOM tooltip.
  // Tooltip uses FIXED positioning to escape any parent overflow clipping.
  const updateHoverFromEvent = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return;
    const svgRect = svg.getBoundingClientRect();
    const scaleX = svgRect.width / W;
    const svgX = (clientX - svgRect.left) / scaleX;
    // Clamp to plot area, then find nearest of 12 slots.
    const clamped = Math.min(Math.max(svgX, padL), W - padR);
    const t = (clamped - padL) / innerW;
    const idx = Math.max(0, Math.min(11, Math.round(t * 11)));
    setHoverIdx(idx);
    // Fixed screen coords: anchor tooltip above the hairline point.
    const pointScreenX = svgRect.left + xFor(idx) * scaleX;
    const pointScreenY = svgRect.top + (padT + innerH * 0.15) * (svgRect.height / H);
    setTipPos({ left: pointScreenX, top: pointScreenY });
  };

  const money0 = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const tip = hoverIdx != null ? {
    idx: hoverIdx,
    monthLabel: `${monthFull[hoverIdx]} ${year}`,
    std_rev: stdRev[hoverIdx], spc_rev: spcRev[hoverIdx], tot_rev: totRev[hoverIdx],
    std_exp: stdExp[hoverIdx], spc_exp: spcExp[hoverIdx], tot_exp: totExp[hoverIdx],
  } : null;
  const tipNet = tip ? tip.tot_rev - tip.tot_exp : 0;

  return (
    <div data-testid="dashboard-revenue-trend" className="mt-4 rounded-xl border border-zinc-200 bg-white p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-700">
            <TrendingUp size={12} /> Monthly Trend · {year}
          </div>
          <h2 className="mt-1 text-lg font-semibold tracking-tight text-zinc-900">Revenue vs Expenses</h2>
        </div>
        <div className="flex max-w-full flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500">
          {SERIES.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5">
              <svg width="14" height="6" className="flex-shrink-0"><line x1="0" y1="3" x2="14" y2="3" stroke={s.color} strokeWidth="2.5" strokeDasharray={s.dash} /></svg>
              {s.label}
            </span>
          ))}
        </div>
      </div>
      <div ref={wrapRef} className="relative mt-4 -mx-2 overflow-x-auto">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          preserveAspectRatio="none"
          className="min-w-[520px]"
          role="img"
          aria-label={`Monthly revenue and expenses trend for ${year}`}
          onMouseMove={(e) => updateHoverFromEvent(e.clientX, e.clientY)}
          onMouseLeave={() => setHoverIdx(null)}
          onTouchStart={(e) => { const t = e.touches[0]; if (t) updateHoverFromEvent(t.clientX, t.clientY); }}
          onTouchMove={(e) => { const t = e.touches[0]; if (t) updateHoverFromEvent(t.clientX, t.clientY); }}
        >
          <defs>
            <linearGradient id="revTrendFill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#10B981" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* horizontal gridlines + Y labels */}
          {gridSteps.map((f) => {
            const y = padT + innerH - innerH * f;
            const value = niceMax * f;
            return (
              <g key={f}>
                <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="#F1F5F9" strokeWidth="1" />
                <text x={padL - 8} y={y + 3} textAnchor="end" fontSize="10" fill="#94A3B8">
                  {value >= 1000 ? `$${(value / 1000).toFixed(0)}k` : `$${value}`}
                </text>
              </g>
            );
          })}

          {/* hover hairline + highlight rings on totals */}
          {hoverIdx != null && (
            <g pointerEvents="none">
              <line
                x1={xFor(hoverIdx)} x2={xFor(hoverIdx)}
                y1={padT} y2={padT + innerH}
                stroke="#94A3B8" strokeWidth="1" strokeDasharray="3 3"
              />
              <circle cx={xFor(hoverIdx)} cy={yFor(totRev[hoverIdx] || 0)} r="6.5" fill="none" stroke="#10B981" strokeWidth="1.5" opacity="0.7" />
              <circle cx={xFor(hoverIdx)} cy={yFor(totExp[hoverIdx] || 0)} r="6.5" fill="none" stroke="#EF4444" strokeWidth="1.5" opacity="0.7" />
            </g>
          )}

          {/* area under TOTAL revenue line (for the sense of the green trend) */}
          <path d={areaD} fill="url(#revTrendFill)" pointerEvents="none" />
          {/* All 6 series — draw expenses first so total revenue sits above */}
          {SERIES.map((s) => (
            <path key={s.key} d={s.path} fill="none" stroke={s.color} strokeWidth={s.key.startsWith("tot_") ? 2.5 : 1.75} strokeLinecap="round" strokeLinejoin="round" strokeDasharray={s.dash} pointerEvents="none" opacity={s.key.startsWith("tot_") ? 1 : 0.85} />
          ))}

          {/* Month dots for TOTAL revenue (emerald) + labels */}
          {totRev.map((v, i) => (
            <g key={`r-${i}`} pointerEvents="none">
              <circle
                cx={xFor(i)}
                cy={yFor(v)}
                r={v > 0 ? 4 : 3}
                fill={v > 0 ? "#10B981" : "#E5E7EB"}
                stroke="white"
                strokeWidth="2"
              />
              <text x={xFor(i)} y={H - 8} textAnchor="middle" fontSize="10" fill="#71717A">{monthNames[i]}</text>
            </g>
          ))}
          {/* Month dots for TOTAL expenses (red) */}
          {totExp.map((v, i) => (
            <circle
              key={`e-${i}`}
              cx={xFor(i)}
              cy={yFor(v)}
              r={v > 0 ? 4 : 3}
              fill={v > 0 ? "#EF4444" : "#E5E7EB"}
              stroke="white"
              strokeWidth="2"
              pointerEvents="none"
            />
          ))}

          {/* Invisible full-height hit zones per month for reliable hover on the labels area too */}
          {Array.from({ length: 12 }).map((_, i) => {
            const x = xFor(i) - (innerW / 22);
            return (
              <rect
                key={`hit-${i}`}
                x={x}
                y={padT}
                width={innerW / 11}
                height={innerH + padB}
                fill="transparent"
              />
            );
          })}
        </svg>

        {/* Floating tooltip (fixed-positioned so it escapes overflow clipping) */}
        {tip && (
          <div
            data-testid="chart-tooltip"
            className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs shadow-lg"
            style={{ left: tipPos.left, top: tipPos.top - 6, minWidth: 200 }}
          >
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{tip.monthLabel}</div>
            <TipRow color="#0066FF" label="Std Rev" value={money0(tip.std_rev)} />
            <TipRow color="#F59E0B" label="Special Rev" value={money0(tip.spc_rev)} />
            <TipRow color="#10B981" label="Total Rev" value={money0(tip.tot_rev)} bold />
            <div className="my-1 border-t border-zinc-100" />
            <TipRow color="#93C5FD" label="Std Exp" value={money0(tip.std_exp)} dashed />
            <TipRow color="#FDE68A" label="Special Exp" value={money0(tip.spc_exp)} dashed />
            <TipRow color="#EF4444" label="Total Exp" value={money0(tip.tot_exp)} bold />
            <div className="mt-1 flex items-center justify-between gap-3 border-t border-zinc-100 pt-1">
              <span className="text-zinc-500">Net</span>
              <span data-testid="chart-tooltip-net" className={`font-mono font-semibold ${tipNet >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                {tipNet < 0 ? `-${money0(Math.abs(tipNet))}` : money0(tipNet)}
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="mt-2 text-[11px] text-zinc-400">Hover anywhere on the chart to see exact revenue, expenses and net for that month.</div>
    </div>
  );
}

function TipRow({ color, label, value, bold, dashed }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="inline-flex items-center gap-1.5 text-zinc-700">
        <svg width="12" height="6" className="flex-shrink-0"><line x1="0" y1="3" x2="12" y2="3" stroke={color} strokeWidth="2" strokeDasharray={dashed ? "3 2" : "0"} /></svg>
        {label}
      </span>
      <span className={`font-mono ${bold ? "font-semibold text-zinc-900" : "text-zinc-600"}`}>{value}</span>
    </div>
  );
}

function RevenueModal({ initial, defaultMonth, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  // When admin selects a past month and hits "Add to Jul", pre-fill the date to the 1st
  // of that month so they don't have to change it.
  const initialDate = initial?.date || (defaultMonth ? `${defaultMonth}-01` : today);
  const [form, setForm] = useState(initial || { amount: "", label: "", date: initialDate, entry_type: "standard" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
    setSaving(true);
    try {
      const payload = {
        amount: amt,
        label: form.label?.trim() || "",
        date: form.date || today,
        entry_type: form.entry_type || "standard",
      };
      if (initial) await api.put(`/revenue/entries/${initial.id}`, payload);
      else await api.post("/revenue/entries", payload);
      toast.success(initial ? "Updated" : "Logged");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="revenue-modal" className="w-full max-w-md rounded-2xl bg-white p-7 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h2 className="text-2xl font-semibold tracking-tight text-zinc-900">
            {initial ? "Edit Revenue" : "Add Revenue"}
          </h2>
          <button onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
        </div>

        <div className="mt-5 flex flex-col gap-4">
          <label className="text-sm">
            <span className="text-xs font-medium text-zinc-600">Amount ($)</span>
            <input
              data-testid="revenue-form-amount"
              autoFocus
              type="number"
              step="0.01"
              min="0"
              value={form.amount}
              onChange={(e) => set("amount", e.target.value)}
              className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            />
          </label>
          <label className="text-sm">
            <span className="text-xs font-medium text-zinc-600">Label <span className="text-zinc-400">(optional)</span></span>
            <input
              value={form.label || ""}
              onChange={(e) => set("label", e.target.value)}
              placeholder="Acme retainer · Logo project · Hosting"
              className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            />
          </label>
          <label className="text-sm">
            <span className="text-xs font-medium text-zinc-600">Type</span>
            <select
              data-testid="revenue-form-type"
              value={form.entry_type || "standard"}
              onChange={(e) => set("entry_type", e.target.value)}
              className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            >
              <option value="standard">Standard project</option>
              <option value="special">Special project</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="text-xs font-medium text-zinc-600">Date</span>
            <input
              type="date"
              value={form.date || ""}
              onChange={(e) => set("date", e.target.value)}
              className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            />
          </label>
        </div>

        <div className="mt-7 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">Cancel</button>
          <button data-testid="revenue-save" onClick={save} disabled={saving} className="rounded-md bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
